import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { FaCheckCircle, FaExclamationTriangle, FaBan, FaCog, FaLightbulb } from 'react-icons/fa';

// Skal matche PRACTICAL_MAX_BRIX i /app/backend/validate_recipe_machine.py.
const PRACTICAL_MAX_BRIX_DISPLAY = 30;

const MachineValidationAlert = ({ recipe, selectedMachine = null }) => {
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (recipe && recipe.target_brix && recipe.base_volume_ml) {
      validateRecipe();
    }
  }, [recipe?.target_brix, recipe?.base_volume_ml, selectedMachine]);

  const validateRecipe = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/validate-recipe-machine`, {
        recipe: {
          target_brix: parseFloat(recipe.target_brix) || 13,
          base_volume_ml: parseFloat(recipe.base_volume_ml) || 1000
        },
        machine_name: selectedMachine
      });
      
      setValidation(response.data);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          <span className="text-blue-700">Validerer opskrift mod maskiner...</span>
        </div>
      </div>
    );
  }

  if (!validation) return null;

  const hasWarnings = validation.warnings && validation.warnings.length > 0;
  const hasRecommendations = validation.recommendations && validation.recommendations.length > 0;
  const hasCriticalAny = validation.warnings?.some(w => w.includes('🚫'));
  const suitableMachines = validation.suitable_machines || [];
  // Feb 2026 (PRD #52): nuanceret severity via 4 tiers fra backend.
  // `recipe_level_critical` = kun ægte ekstremer (brix > 30 eller < 0).
  // Per-maskine mismatch (fx forkert tank-størrelse) er IKKE kritisk.
  const severity = validation.severity_summary || { ideal: 0, practical: 0, warning: 0, critical: 0 };
  const recipeLevelCritical = validation.recipe_level_critical === true;
  const hasSuitable = suitableMachines.length > 0;
  // "Rød banner" (kritisk) vises KUN når opskriften i sig selv er ubrugelig
  // — dvs. reelle ekstreme Brix-værdier. Hvis en enkelt for-lille tank
  // eller for-lav Brix på 1 maskine udløser 🚫, men andre maskiner
  // kan håndtere det → downgrade til gul advarsel.
  const hasCritical = recipeLevelCritical || (hasCriticalAny && !hasSuitable);

  // If no warnings and no critical issues, show success message only
  if (!hasWarnings && validation.valid) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <FaCheckCircle className="text-green-600 text-xl" />
          <div>
            <div className="font-medium text-green-800">Opskriften er kompatibel!</div>
            <div className="text-sm text-green-600">
              {suitableMachines.length > 0 && (
                <span>{suitableMachines.length} maskiner kan håndtere denne opskrift perfekt</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Kompakt severity-badge-linje (feb 2026 — PRD #52). Viser altid
  // hvor mange maskiner der ligger i hvert af de 4 lag så brugeren
  // hurtigt kan se "der er egnede maskiner, bare ikke ideelle".
  const severityBadges = (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="brix-severity-summary">
      {severity.ideal > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
          🟢 {severity.ideal} ideelt
        </span>
      )}
      {severity.practical > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
          🔵 {severity.practical} praktisk
        </span>
      )}
      {severity.warning > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
          🟡 {severity.warning} advarsel
        </span>
      )}
      {recipeLevelCritical && (
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200">
          🔴 kritisk
        </span>
      )}
    </div>
  );

  return (
    <div className={`border rounded-lg overflow-hidden ${
      hasCritical ? 'bg-red-50 border-red-300' :
      hasWarnings ? 'bg-yellow-50 border-yellow-300' :
      selectedMachine ? 'bg-emerald-50 border-emerald-300' :
      'bg-blue-50 border-blue-300'
    }`}>
      {/* Header */}
      <div
        className={`p-4 cursor-pointer flex items-center justify-between ${
          hasCritical ? 'bg-red-100' :
          hasWarnings ? 'bg-yellow-100' :
          selectedMachine ? 'bg-emerald-100' :
          'bg-blue-100'
        }`}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-3">
          {hasCritical ? (
            <FaBan className="text-red-600 text-xl" />
          ) : hasWarnings ? (
            <FaExclamationTriangle className="text-yellow-600 text-xl" />
          ) : (
            <FaCheckCircle className="text-green-600 text-xl" />
          )}
          <div>
            <div className={`font-medium ${
              hasCritical ? 'text-red-800' : hasWarnings ? 'text-yellow-800' :
              selectedMachine ? 'text-green-800' : 'text-blue-800'
            }`}>
              {hasCritical ? (recipeLevelCritical ? 'Kritisk: Opskriften kan ikke bruges' : 'Kritiske problemer fundet') :
               hasSuitable && severity.warning > 0 && severity.ideal === 0 && severity.practical === 0 ? `Høj Brix — ${hasSuitable ? suitableMachines.length : 0} maskiner kan bruges med længere frysetid` :
               hasSuitable && severity.warning > 0 ? `${severity.ideal + severity.practical} maskiner ideelt/praktisk — resten kræver længere frysetid` :
               hasWarnings && selectedMachine ? `Advarsler for ${selectedMachine}` :
               hasWarnings ? 'Vejledning til denne opskrift' :
               selectedMachine ? `Passer til din maskine (${selectedMachine})` :
               'Maskinkompatibilitet'}
            </div>
            <div className={`text-sm mb-1 ${
              hasCritical ? 'text-red-600' : hasWarnings ? 'text-yellow-700' :
              selectedMachine ? 'text-green-700' : 'text-blue-600'
            }`}>
              {selectedMachine
                ? `Analyseret mod din valgte maskine`
                : `${validation.machines_checked} maskiner tjekket`}
              {recipeLevelCritical && ' — værdien ligger uden for praktisk område'}
              {!recipeLevelCritical && hasCriticalAny && !hasSuitable && ' — ingen maskine kan håndtere kombinationen'}
              {!hasCritical && hasCriticalAny && hasSuitable && ' — vælg en af de egnede maskiner nedenfor'}
            </div>
            {!selectedMachine && severityBadges}
          </div>
        </div>
        <button 
          type="button"
          className="text-gray-500 hover:text-gray-700"
        >
          {collapsed ? '▼' : '▲'}
        </button>
      </div>

      {/* Content */}
      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Warnings */}
          {hasWarnings && (
            <div className="space-y-2">
              <div className="font-medium text-gray-700 flex items-center gap-2">
                <FaExclamationTriangle className="text-yellow-600" />
                Advarsler:
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {validation.warnings.map((warning, idx) => {
                  const isCritical = warning.includes('🚫');
                  const isWarning = warning.includes('⚠️');
                  const isTime = warning.includes('⏱️');
                  
                  return (
                    <div 
                      key={idx} 
                      className={`text-sm p-2 rounded ${
                        isCritical ? 'bg-red-100 text-red-800' :
                        isWarning ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {warning.replace(/\*\*/g, '')}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {hasRecommendations && (
            <div className="space-y-2">
              <div className="font-medium text-gray-700 flex items-center gap-2">
                <FaLightbulb className="text-blue-600" />
                Anbefalinger:
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {validation.recommendations.slice(0, 10).map((rec, idx) => (
                  <div key={idx} className="text-sm p-2 bg-blue-50 text-blue-800 rounded">
                    {rec.replace(/\*\*/g, '')}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suitable Machines */}
          {suitableMachines.length > 0 && (
            <div className="space-y-2">
              <div className="font-medium text-gray-700 flex items-center gap-2">
                <FaCog className="text-green-600" />
                Egnede maskiner (Top {Math.min(5, suitableMachines.length)}):
              </div>
              <div className="grid gap-2">
                {suitableMachines.slice(0, 5).map((machine, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white border border-gray-200 rounded p-3 text-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-gray-800">{machine.name}</div>
                        <div className="text-xs text-gray-500 capitalize">{machine.category}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium text-green-600">
                          Score: {machine.score}/100
                        </div>
                        <div className="text-xs text-gray-500">{machine.capacity_l}L</div>
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      Brix: {machine.recommended_brix}
                    </div>
                    {machine.reasons && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {machine.reasons.map((reason, ridx) => (
                          <span key={ridx} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            {reason}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call to action — kun for ægte kritiske tilfælde */}
          {hasCritical && recipeLevelCritical && (
            <div className="bg-red-100 border border-red-300 rounded p-3 text-sm text-red-800">
              <strong>⚠️ Vigtig:</strong> Denne opskrift ligger uden for det praktiske område (over {PRACTICAL_MAX_BRIX_DISPLAY}°Bx eller under 0°Bx). Fortynd med vand eller is før frysning.
            </div>
          )}
          {hasCritical && !recipeLevelCritical && (
            <div className="bg-red-100 border border-red-300 rounded p-3 text-sm text-red-800">
              <strong>⚠️ Vigtig:</strong> Ingen af de tjekkede maskiner passer til denne kombination af Brix og volumen. Overvej at justere volumen eller vælg en maskine der matcher.
            </div>
          )}
          {!hasCritical && severity.warning > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded p-3 text-sm text-yellow-900">
              <strong>💡 Tip:</strong> Brix ligger højt for nogle maskiner. De kan stadig fryse opskriften, men det tager længere tid og resultatet bliver blødere. Fortynd med vand eller is hvis du vil have hurtigere/hårdere slush.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MachineValidationAlert;
